import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PhoneOff, PhoneMissed, WifiOff, Power, CheckCircle2, Phone, CalendarDays, Users, Zap, BarChart3, ChevronRight } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { SubscriptionPlan } from '../data/subscriptionPlans';
// Components
import { HomeHeader } from '../components/home/HomeHeader';
import { CallForwardingCard } from '../components/home/CallForwardingCard';
import { TodaysOverview } from '../components/home/TodaysOverview';
import { ActivationOverlays } from '../components/home/ActivationOverlays';
interface HomeScreenProps {
  onNavigate: (screen: string) => void;
  isActivated?: boolean;
  /** Answering-rule ids enabled during iOS activation (e.g. ['busy','unanswered']). */
  enabledRules?: string[];
  /** Selected package, used to keep allowance summaries in sync with billing. */
  plan?: SubscriptionPlan;
  /** Opens the shared mobile-or-landline forwarding selector. */
  onChooseForwardingNumber?: () => void;
  /** Enable an additional answering rule via the same activation flow. */
  onAddRule?: (conditionId: string) => void;
  /** Deactivate an answering rule via the deactivation flow. */
  onDeactivateRule?: (conditionId: string) => void;
}
type ForwardTarget = 'vexu' | 'other' | 'disabled';
interface ForwardingScenario {
  id: string;
  name: string;
  icon: React.ElementType;
  status: ForwardTarget;
  otherNumber?: string;
}
export function HomeScreen({
  onNavigate,
  isActivated: activatedProp = false,
  enabledRules = [],
  plan,
  onChooseForwardingNumber,
  onAddRule,
  onDeactivateRule
}: HomeScreenProps) {
  const {
    isLight
  } = useTheme();
  const VEXU_NUMBER = '+1 (555) 019-8472';
  // ─── Core state ───
  const [scenarios, setScenarios] = useState<ForwardingScenario[]>([{
    id: 'busy',
    name: 'When Busy',
    icon: PhoneOff,
    status: 'other',
    otherNumber: '+1 (555) 000-0000'
  }, {
    id: 'unanswered',
    name: 'When Unanswered',
    icon: PhoneMissed,
    status: 'disabled'
  }, {
    id: 'unreachable',
    name: 'When Unreachable',
    icon: WifiOff,
    status: 'disabled'
  }, {
    id: 'off',
    name: 'When Phone Off',
    icon: Power,
    status: 'disabled'
  }]);
  // ─── UI state ───
  const [successToast, setSuccessToast] = useState<string | null>(null);
  const [forwardingExpanded, setForwardingExpanded] = useState(false);
  const [hasData] = useState(true);
  // ─── Activation state ───
  const [isActivated, setIsActivated] = useState(activatedProp);
  useEffect(() => {
    setIsActivated(activatedProp);
    if (activatedProp) {
      setSyncPhase('synced');
      setForwardingExpanded(true);
    }
  }, [activatedProp]);
  // Reflect rules enabled during iOS activation onto the answering-rules card.
  useEffect(() => {
    if (!isActivated) return;
    const enableAll = enabledRules.includes('all');
    setScenarios(prev => prev.map(s => enableAll || enabledRules.includes(s.id) ? {
      ...s,
      status: 'vexu',
      otherNumber: undefined
    } : {
      ...s,
      status: 'disabled'
    }));
  }, [enabledRules, isActivated]);
  // ─── Flow states ───
  const [syncPhase, setSyncPhase] = useState<'idle' | 'confirm' | 'scanning' | 'synced'>(activatedProp ? 'synced' : 'idle');
  const [scanProgress, setScanProgress] = useState(0);
  const SCAN_STEPS = [{
    msg: 'Reading your phone settings...',
    sub: 'Checking call forwarding'
  }, {
    msg: 'Checking "When Busy"...',
    sub: '1 of 4 reviewed'
  }, {
    msg: 'Reviewing "When Unanswered"...',
    sub: '2 of 4 reviewed'
  }, {
    msg: 'Reviewing "When Unreachable"...',
    sub: '3 of 4 reviewed'
  }, {
    msg: 'Verifying "When Phone Off"...',
    sub: 'Matching numbers with Vexu.ai'
  }, {
    msg: 'Finalizing settings check...',
    sub: '4 of 4 completed'
  }];
  const [scanText, setScanText] = useState(SCAN_STEPS[0].msg);
  const [switchPhase, setSwitchPhase] = useState<'confirm' | 'activating' | 'done' | null>(null);
  const [switchTarget, setSwitchTarget] = useState<ForwardingScenario | null>(null);
  const [disablePhase, setDisablePhase] = useState<'confirm' | 'disabling' | 'done' | null>(null);
  const [disableTarget, setDisableTarget] = useState<ForwardingScenario | null>(null);
  // ─── Handlers ───
  const startSync = () => {
    if (!isActivated) {
      // Subscription starts with plan selection and required consent before any
      // carrier setup begins.
      onNavigate('planSelection');
    } else {
      setSyncPhase('synced');
      setForwardingExpanded(true);
    }
  };
  const confirmSync = () => {
    setSyncPhase('scanning');
    setScanProgress(0);
    setScanText(SCAN_STEPS[0].msg);
    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      if (currentStep < SCAN_STEPS.length) {
        setScanProgress(currentStep / SCAN_STEPS.length * 100);
        setScanText(SCAN_STEPS[currentStep].msg);
      } else {
        clearInterval(interval);
        setScanProgress(100);
        setTimeout(() => {
          setSyncPhase('synced');
          setForwardingExpanded(true);
        }, 2000);
      }
    }, 2200);
  };
  const handleEnable = (scenario: ForwardingScenario) => {
    // Enable an additional rule using the SAME guided activation flow,
    // seeded with just this condition.
    if (onAddRule) {
      onAddRule(scenario.id);
      return;
    }
    setSwitchTarget(scenario);
    setSwitchPhase('confirm');
  };
  const confirmSwitch = () => {
    setSwitchPhase('activating');
    setTimeout(() => {
      setSwitchPhase('done');
      setTimeout(() => {
        const targetName = switchTarget?.name || '';
        setScenarios(prev => prev.map(s => s.id === switchTarget?.id ? {
          ...s,
          status: 'vexu'
        } : s));
        setSwitchPhase(null);
        setSwitchTarget(null);
        setSuccessToast(`"${targetName}" is now forwarded to Vexu AI`);
        setTimeout(() => setSuccessToast(null), 3000);
      }, 2000);
    }, 3000);
  };
  const handleDisable = (scenario: ForwardingScenario) => {
    if (onDeactivateRule) {
      onDeactivateRule(scenario.id);
      return;
    }
    setDisableTarget(scenario);
    setDisablePhase('confirm');
  };
  const confirmDisable = () => {
    setDisablePhase('disabling');
    setTimeout(() => {
      setDisablePhase('done');
      setTimeout(() => {
        setScenarios(prev => prev.map(s => s.id === disableTarget?.id ? {
          ...s,
          status: 'disabled'
        } : s));
        setDisablePhase(null);
        setDisableTarget(null);
      }, 2000);
    }, 2600);
  };
  // When the user chose "Answer All Calls", present the answering-rules card as
  // a single consolidated "All Incoming Calls" item with one Deactivate action,
  // instead of four identical rows the user can't coherently manage.
  const allMode = isActivated && enabledRules.includes('all');
  const displayScenarios: ForwardingScenario[] = allMode ? [{
    id: 'all',
    name: 'All Incoming Calls',
    icon: Phone,
    status: 'vexu'
  }] : scenarios;
  return <div className="h-full overflow-y-auto pb-36 relative" data-id="element-2928" data-mp="2qt1t7apv47of">
      {/* Background orbs */}
      <div className={`absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-vexu/10' : 'bg-vexu/5'}`} data-id="element-2929" data-mp="2mq5gu1qx17sx" />

      <div className="p-6 space-y-8 relative z-10" data-id="element-2930" data-mp="63m72t1l9754">
        <HomeHeader onNavigate={onNavigate} data-id="element-2931" data-mp="1z4r74ycndzm" />

        <CallForwardingCard syncPhase={syncPhase} scenarios={displayScenarios} forwardingExpanded={forwardingExpanded} setForwardingExpanded={setForwardingExpanded} isActivated={isActivated} startSync={startSync} handleDisable={handleDisable} handleEnable={handleEnable} onChooseForwardingNumber={onChooseForwardingNumber ?? (() => onNavigate('forwardingSetup'))} onNavigate={onNavigate} data-id="element-2932" data-mp="22iqu5fmp93d" />

        {/* Unlock Vexu Features (Not Activated) */}
        {!isActivated && <div className="space-y-3" data-id="element-2933" data-mp="2o7knf8rd5jje">
            <h2 className={`text-sm font-bold uppercase tracking-wider ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-2934" data-mp="2o8lz9fg7ea26">
              Unlock Vexu Features
            </h2>
            <div className="space-y-3" data-id="element-2935" data-mp="tul2d047rx4q">
              {[{
            icon: Phone,
            title: 'AI Call Handling',
            desc: 'Never miss an opportunity with 24/7 AI answering.',
            color: 'text-blue-500',
            bg: isLight ? 'bg-blue-50' : 'bg-blue-500/10'
          }, {
            icon: CalendarDays,
            title: 'Smart Scheduling',
            desc: 'Let AI book meetings directly to your calendar.',
            color: 'text-purple-500',
            bg: isLight ? 'bg-purple-50' : 'bg-purple-500/10'
          }, {
            icon: Users,
            title: 'Caller Experience',
            desc: 'Customize how AI greets different contacts.',
            color: 'text-vexu',
            bg: isLight ? 'bg-vexu/10' : 'bg-vexu/10'
          }].map((feat, i) => {
            const Icon = feat.icon;
            return <div key={i} className={`flex items-center gap-4 p-4 rounded-2xl border backdrop-blur-xl transition-colors ${isLight ? 'bg-white/80 border-slate-200/60 shadow-sm' : 'bg-[#111111]/80 border border-white/5'}`} data-id="element-2936" data-mp="2ajk8cr00be6h" data-mpi={i}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${feat.bg}`} data-id="element-2937" data-mp="1402b52o0necz">
                      <Icon className={`w-5 h-5 ${feat.color}`} data-id="element-2938" data-mp="1dzbcigl7nv9d" />
                    </div>
                    <div data-id="element-2939" data-mp="1rqyackusxbu9">
                      <div className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2940" data-mp="1rq6f5b549g4i">
                        {feat.title}
                      </div>
                      <div className={`text-xs mt-0.5 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2941" data-mp="21nuksw49g81g">
                        {feat.desc}
                      </div>
                    </div>
                  </div>;
          })}
            </div>
            <button data-tour="activate-button" onClick={() => onNavigate('planSelection')} className="w-full py-3 rounded-2xl text-sm font-bold text-center bg-vexu text-white flex items-center justify-center gap-2" data-id="element-2942" data-mp="2f5xgcdpiqms">
              <Zap className="w-4 h-4" data-id="element-2943" data-mp="24a3cfh6k1fjk" />
              Activate Vexu Now
            </button>
          </div>}

        {/* Today's Overview (Activated only) */}
        {isActivated && <TodaysOverview hasData={hasData} onNavigate={onNavigate} minutesTotal={plan?.minutes ?? 180} data-id="element-2944" data-mp="1v83xqvj0injs" />}

        {/* Quick Actions (Activated only) */}
        {isActivated && <div className="space-y-3" data-id="element-2945" data-mp="1qakjvwq88csd">
            <h3 className={`text-sm font-bold uppercase tracking-wider ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-2946" data-mp="1qbn6hsgint2h">
              Quick Actions
            </h3>
            <div className="grid grid-cols-2 gap-2.5" data-id="element-2947" data-mp="3knk2zzttfp4e">
              {[{
            icon: Users,
            label: 'Caller\nExperience',
            screen: 'callerExperience',
            accent: isLight ? 'text-vexu-600 bg-vexu/10' : 'text-vexu bg-vexu/10'
          }, {
            icon: BarChart3,
            label: 'View\nUsage',
            screen: 'usage',
            accent: isLight ? 'text-amber-500 bg-amber-50' : 'text-amber-400 bg-amber-500/10'
          }].map(action => {
            const Icon = action.icon;
            return <button key={action.label} onClick={() => onNavigate(action.screen)} className={`flex flex-col items-center gap-2.5 p-4 rounded-2xl transition-colors text-center ${isLight ? 'bg-white/70 border border-slate-100 active:bg-slate-50' : 'bg-white/[0.03] border border-white/5 active:bg-white/[0.06]'}`} data-id="element-2948" data-mp="3s5rq0sfuvewf">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${action.accent}`} data-id="element-2949" data-mp="s3bddazrc913">
                      <Icon className="w-5 h-5" data-id="element-2950" data-mp="110vg6od9qddt" />
                    </div>
                    <span className={`text-[11px] font-semibold leading-tight whitespace-pre-line ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-2951" data-mp="1619g1j4puh0r">
                      {action.label}
                    </span>
                  </button>;
          })}
            </div>
          </div>}
      </div>

      <ActivationOverlays syncPhase={syncPhase} scanProgress={scanProgress} scanText={scanText} confirmSync={confirmSync} setSyncPhase={setSyncPhase} switchPhase={switchPhase} switchTarget={switchTarget} confirmSwitch={confirmSwitch} setSwitchPhase={setSwitchPhase} setSwitchTarget={setSwitchTarget} disablePhase={disablePhase} disableTarget={disableTarget} confirmDisable={confirmDisable} setDisablePhase={setDisablePhase} setDisableTarget={setDisableTarget} VEXU_NUMBER={VEXU_NUMBER} data-id="element-2952" data-mp="1juh799ovxahx" />

      {/* Success Toast */}
      <AnimatePresence data-id="element-2953" data-mp="308k1rx77k4f2">
        {successToast && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 20
      }} transition={{
        type: 'spring',
        stiffness: 400,
        damping: 30
      }} className="absolute bottom-28 left-1/2 -translate-x-1/2 z-50" data-id="element-2954" data-mp="2chwp6ymckqm6">
            <div className={`flex items-center gap-2.5 px-5 py-3 rounded-2xl shadow-lg backdrop-blur-xl ${isLight ? 'bg-slate-900 text-white shadow-slate-900/20' : 'bg-white text-black shadow-black/30'}`} data-id="element-2955" data-mp="u86ff555ktk">
              <CheckCircle2 className="w-5 h-5 text-vexu flex-shrink-0" data-id="element-2956" data-mp="99xgjsfrscci" />
              <span className="text-sm font-medium" data-id="element-2957" data-mp="3qhj2g3k9q6ra">{successToast}</span>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}